import { useCallback } from "react"
import { setMemoryReference, useMemoryReference } from "lib/@hooks/use-memory-reference"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

// Selection is slightly more complicated than using just the id of the item,
// as the same schedule (as represented by its _id) can appear in multiple parts
// of the tree, such as in search results or in other sets.  To cope with this
// we use an object to represent selections where each key is the _id of the schedule
// that has been selected and the value of the key is the "hint id" - this is a unique
// id that combines the folder and the item within it.  Using this method we can
// make sure schedules are only selected once, but still have some kind of way of understanding
// which one is currently selected; this means we can properly provide breadcrumbs.

// The useSelection hook returns a select function in its second position - this will take either
// an _id or an array where the first element should be the _id and the second the hint id.

// useSelectionReferenceType & getSelectedItemsKey hooks were added so that we can use useSelection and selectItems without
// affecting the global selection in multi select manual mapping. If we don't pass a referenceType
// they default to "selection" & "selectedItems" respectively.

export const SELECTION_REFERENCE_TYPE = "selection"

export function useSelection(referenceType = undefined) {
    const selectedReferenceType = useSelectionReferenceType(referenceType)
    const selectedItemsKey = getSelectedItemsKey(selectedReferenceType)
    const [reference, , save] = useMemoryReference({ [selectedItemsKey]: {} }, selectedReferenceType)
    reference[selectedItemsKey] ||= {}
    isSelected.absolute = isAbsolutelySelected
    return [useCallback(isSelected, [reference, selectedItemsKey]), select, reference[selectedItemsKey] || {}]

    function isSelected(idAndHint) {
        const id = Array.isArray(idAndHint) ? idAndHint[0] : idAndHint
        const value = reference[selectedItemsKey][id]
        if (value === undefined || value === false) return false
        return Array.isArray(value) ? !!value.length : !!value
    }

    function isAbsolutelySelected(idAndHint) {
        const id = Array.isArray(idAndHint) ? idAndHint[0] : idAndHint
        const hint = Array.isArray(idAndHint) ? idAndHint[1] : true
        const value = reference[selectedItemsKey][id]
        if (value === undefined || value === false) return false
        return Array.isArray(value) ? value.includes(hint) : value === hint
    }

    function select(idAndHint, isSelected = true) {
        const id = Array.isArray(idAndHint) ? idAndHint[0] : idAndHint
        const hint = Array.isArray(idAndHint) ? idAndHint[1] : idAndHint
        if (isSelected) {
            save((reference) => ({
                ...reference,
                [selectedItemsKey]: { ...reference[selectedItemsKey], [id]: [hint] },
            }))
        } else {
            save((reference) => ({
                ...reference,
                [selectedItemsKey]: { ...reference[selectedItemsKey], [id]: false },
            }))
        }
    }
}

export function useSelectionReferenceType(referenceType = undefined) {
    const { selectionReferenceType } = useBoundContext()
    return referenceType ?? selectionReferenceType ?? SELECTION_REFERENCE_TYPE
}

export function getSelectedItemsKey(referenceType = SELECTION_REFERENCE_TYPE) {
    return referenceType === SELECTION_REFERENCE_TYPE ? "selectedItems" : `${referenceType}SelectedItems`
}

/**
 * @callback SelectorFunction
 * @param {Record<string, string|boolean>} selectedItems - the currently selected items
 * @returns {Record<string, string|boolean>} the updated selections
 */

/**
 * Select current items
 * @param {SelectorFunction} selector - the function that will return the selected items
 */
export function selectItems(selector, referenceType = SELECTION_REFERENCE_TYPE) {
    const selectedItemsKey = getSelectedItemsKey(referenceType)
    setMemoryReference((reference) => {
        reference[selectedItemsKey] ??= {}
        let selectedItems = selector(reference[selectedItemsKey])
        if (Array.isArray(selectedItems)) {
            selectedItems = Object.fromEntries(selectedItems.map((id) => [id, id]))
        }
        reference[selectedItemsKey] = selectedItems
        return reference
    }, referenceType)
}
